import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { OFFICE_CHANGED_EVENTS, broadcastOfficeChanged } from '@/hooks/tasks-sync-channel';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { isQueryableOrganizationId } from '@/lib/organization-id';

import type { Tables } from '@/integrations/supabase/types';

/** What the page reads of an agent: never `*`, since name and role are free text. */
const AGENT_COLUMNS = 'id, name, role, emblem, tools, rule, created_by';

export type OfficeAgentRow = Pick<
  Tables<'office_agents'>,
  'id' | 'name' | 'role' | 'emblem' | 'tools' | 'rule' | 'created_by'
>;

/** A new agent as the page saves it. The id is minted by the page, so it can show the agent at once. */
export type NewOfficeAgent = Pick<
  Tables<'office_agents'>,
  'id' | 'name' | 'role' | 'emblem' | 'rule'
> & { readonly tools: readonly string[] };

/**
 * Under the `pulse-ai` root, which is never written to the browser's storage
 * (`shouldDehydrateQuery` in lib/query-persistence.ts): what a colleague typed stays out of it.
 */
export const officeAgentKeys = {
  list: (organizationId: string) => ['pulse-ai', 'office-agents', organizationId] as const,
  /** Every save and archive, so the last one under way is the one that reads the list again. */
  writes: (organizationId: string) =>
    ['pulse-ai', 'office-agents', organizationId, 'write'] as const,
};

async function fetchOfficeAgents(organizationId: string): Promise<OfficeAgentRow[]> {
  const { data, error } = await supabase
    .from('office_agents')
    .select(AGENT_COLUMNS)
    .eq('organization_id', organizationId)
    .is('archived_at', null)
    .order('created_at')
    .order('id');
  if (error) {
    throw error;
  }
  return data;
}

/** One saved row in the list, in place of any copy of it a read already brought. */
function withSaved(rows: readonly OfficeAgentRow[], saved: OfficeAgentRow): OfficeAgentRow[] {
  return rows.some((row) => row.id === saved.id)
    ? rows.map((row) => (row.id === saved.id ? saved : row))
    : [...rows, saved];
}

/**
 * The list with one agent whose archive failed put back where it was, leaving every other row as
 * it now is: another archive or save may have landed since this one began.
 */
function withRestored(
  rows: readonly OfficeAgentRow[],
  previous: readonly OfficeAgentRow[],
  agentId: string,
): OfficeAgentRow[] {
  const lost = previous.find((row) => row.id === agentId);
  if (!lost || rows.some((row) => row.id === agentId)) {
    return [...rows];
  }
  const order = new Map(previous.map((row, index) => [row.id, index]));
  const place = (row: OfficeAgentRow) => order.get(row.id) ?? previous.length;
  return [...rows, lost].sort((left, right) => place(left) - place(right));
}

/**
 * The agents an office has created that are not archived, oldest first. Read again whenever the
 * page mounts or the window regains focus, whatever the app's default freshness, so a colleague's
 * new or removed agent shows when the person comes back to it; but not while one of the person's
 * own saves or archives is out, whose read could put back an agent being archived. The last of
 * those reads the list again when it settles.
 */
export function useOfficeAgents(organizationId: string | null) {
  const queryClient = useQueryClient();
  const mutationKey = officeAgentKeys.writes(String(organizationId));
  const refetchUnlessWriting = () =>
    queryClient.isMutating({ mutationKey }) > 0 ? false : ('always' as const);
  const { data, isLoading, error } = useQuery({
    queryKey: officeAgentKeys.list(String(organizationId)),
    queryFn: async () => await fetchOfficeAgents(String(organizationId)),
    enabled: isQueryableOrganizationId(organizationId),
    refetchOnMount: refetchUnlessWriting,
    refetchOnWindowFocus: refetchUnlessWriting,
  });
  return { data, isLoading, error };
}

/**
 * Saves a new agent to the office. The saved row joins the cached list once, whether or not a
 * read under way brought it first; a failed save adds nothing and says so.
 */
export function useCreateOfficeAgent(organizationId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const queryKey = officeAgentKeys.list(String(organizationId));
  const mutationKey = officeAgentKeys.writes(String(organizationId));

  return useMutation({
    mutationKey,
    mutationFn: async (agent: NewOfficeAgent) => {
      const { data, error } = await supabase
        .from('office_agents')
        .insert({ ...agent, tools: [...agent.tools], organization_id: String(organizationId) })
        .select(AGENT_COLUMNS)
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: async (saved) => {
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.setQueryData<OfficeAgentRow[]>(queryKey, (rows = []) => withSaved(rows, saved));
    },
    onError: () => {
      toast({ title: t('home.agents.saveFailed'), variant: 'destructive' });
    },
    // The saved row is already in the list; the last write under way reads it again, so an
    // archive still out is not undone by this read (see useArchiveOfficeAgent).
    onSettled: async () => {
      // The office's other homes, and this person's other tabs, read the roster again.
      broadcastOfficeChanged(organizationId, OFFICE_CHANGED_EVENTS.agents);
      if (queryClient.isMutating({ mutationKey }) === 1) {
        await queryClient.invalidateQueries({ queryKey, exact: true });
      }
    },
  });
}

/**
 * Archives an agent, taking it off the list at once. An update that reaches no row (already
 * archived, or not this person's to archive) is a failure: the agent comes back and the person is
 * told.
 */
export function useArchiveOfficeAgent(organizationId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const queryKey = officeAgentKeys.list(String(organizationId));
  const mutationKey = officeAgentKeys.writes(String(organizationId));

  return useMutation({
    mutationKey,
    mutationFn: async (agentId: string) => {
      // The server stamps the time and who archived it; the value sent only marks the change.
      const { error } = await supabase
        .from('office_agents')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', agentId)
        .is('archived_at', null)
        .select('id')
        .single();
      if (error) {
        throw error;
      }
    },
    // A read under way is cancelled first, or its answer would put the agent back.
    onMutate: async (agentId) => {
      await queryClient.cancelQueries({ queryKey, exact: true });
      const previous = queryClient.getQueryData<OfficeAgentRow[]>(queryKey);
      queryClient.setQueryData<OfficeAgentRow[]>(queryKey, (rows) =>
        rows?.filter((row) => row.id !== agentId),
      );
      return { previous };
    },
    onError: (_error, agentId, context) => {
      const previous = context?.previous;
      if (previous) {
        queryClient.setQueryData<OfficeAgentRow[]>(queryKey, (rows = []) =>
          withRestored(rows, previous, agentId),
        );
      }
      toast({ title: t('home.agents.removeFailed'), variant: 'destructive' });
    },
    // Read again only once the last save or archive under way has settled: an earlier one's read
    // would still find an agent being archived and put it back on the roster until that settled.
    onSettled: async () => {
      // The office's other homes, and this person's other tabs, read the roster again.
      broadcastOfficeChanged(organizationId, OFFICE_CHANGED_EVENTS.agents);
      if (queryClient.isMutating({ mutationKey }) === 1) {
        await queryClient.invalidateQueries({ queryKey, exact: true });
      }
    },
  });
}
